import { FC, useEffect, useState } from "react";
import styles from "./pilots-table.module.scss";
import { ReactComponent as DoneIcon } from "./assets/done.svg";
import { TableRowActions } from "../../../../ui/tableRowActions/tableRowActions";
import { useParams } from "react-router-dom";
import { useDeletePilotMutation, useGetAllPilotsQuery } from "./pilotsApi";
import { Button } from "../../../../ui/button/Button";
import { PreloaderBar } from "../../../../ui/preloaderBar/PreloaderBar";
import { Snackbar } from "../../../../ui/snackbar/Snackbar";
import { Confirm } from "../../../../ui/confirm/confirm";
import { IPilotList } from "./IPilots";
import { AddPilot } from "./addPilot/addPilot";
import { EditPilot } from "./editPilot/editPilot";

type RowProps = Omit<IPilotList, "worker_id"> & {
  handler: (type: "edit" | "delete", id: number) => boolean;
};

const Row: FC<RowProps> = ({ id, worker_name, worker_number, role, handler }) => {
  return (
    <tr>
      <td>{`${worker_number} | ${worker_name}`}</td>
      <td>{role === "inspector" && <DoneIcon />}</td>
      <td>{role === "intern" && <DoneIcon />}</td>
      <td>
        <TableRowActions id={id} handler={handler} />
      </td>
    </tr>
  );
};

export const PilotsTable: FC = () => {
  const { id } = useParams();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [ID, setID] = useState<number | null>(null);
  const { data, error, isError, isFetching } = useGetAllPilotsQuery(Number(id));

  const [
    deletePilot,
    {
      isError: isDeleteError,
      isLoading: isDeleteProcessing,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeletePilotMutation();

  const create = () => {
    setIsAddOpen(true);
  };

  const rowHandler = (type: "edit" | "delete", id: number) => {
    if (isConfirmOpen) return false;
    setIsUXOpen(true);
    setID(id);
    switch (type) {
      case "delete":
        setIsConfirmOpen(true);
        break;

      case "edit":
        setIsEditOpen(true);
        break;

      default:
        break;
    }
    return true;
  };

  useEffect(() => {
    if (!isDeleteSuccess) return;

    setIsConfirmOpen(false);
    setID(null);
  }, [isDeleteSuccess]);

  return (
    <section className={styles.main}>
      <header className={styles.header}>
        <h2>Летчики-наблюдатели</h2>
        <div className={styles.actions}>
          <Button onClick={create}>Добавить</Button>
        </div>
      </header>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>ФИО, т/н</th>
            <th>Проверяющий</th>
            <th>Стажер</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                worker_name={row.worker_name}
                worker_number={row.worker_number}
                role={row.role}
                flight_work={row.flight_work}
                handler={rowHandler}
              />
            ))}
        </tbody>
      </table>
      {isFetching && <PreloaderBar />}
      {isAddOpen && Number(id) && (
        <AddPilot
          flight_work_id={Number(id)}
          close={() => setIsAddOpen(false)}
        />
      )}
      {isEditOpen && ID && <EditPilot close={() => setIsEditOpen(false)} flight_work_id={Number(id)} id={ID}/>}
      {isError && "status" in error && error.status !== 404 && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>
      )}
      {isConfirmOpen && ID && (
        <Confirm
          confirm={() => deletePilot(ID)}
          reject={() => {
            setIsConfirmOpen(false);
            setID(null);
          }}
          text="Вы действительно хотите удалить запись из системы?"
          isProcessing={isDeleteProcessing}
        />
      )}
      {isDeleteSuccess && isUXOpen && (
        <Snackbar
          className="success"
          isOpen={isDeleteSuccess}
          onClose={() => setIsUXOpen(false)}
        >
          Запись удалена
        </Snackbar>
      )}
      {isDeleteError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isDeleteError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка удаления. Попробуйте позже
        </Snackbar>
      )}
    </section>
  );
};
